package com.exura.chartsJS

import com.exura.util.jso
import org.jetbrains.compose.web.css.CSSColorValue

class ChartScope {
    internal var type: String? = null
    internal var labels: Array<String>? = null

    internal val datasets = mutableListOf<dynamic>()

    internal val options = jso {
        responsive = true
        maintainAspectRatio = false
    }
    internal var plotBackgroundColor: String? = null

    fun plotBackgroundColor(color: String) {
        plotBackgroundColor = color
    }

    fun type(newType: String) {
        type = newType
    }

    fun labels(vararg newLabels: String) {
        labels = newLabels.toList().toTypedArray()
    }
    internal var inferredXAxisType: String? = null

    // ------------------------------------------------------
    // CASE A: XY PLOTS (Linear Axis)
    // Usage: line(listOf(1 to 10, 2 to 20))
    // ------------------------------------------------------
    fun line(data: List<Pair<Number, Number>>, block: LineDatasetScope.() -> Unit = {}) {
        addDataset("line", formatXY(data), "linear", block)
    }

    fun bar(data: List<Pair<Number, Number>>, block: BarDatasetScope.() -> Unit = {}) {
        addDataset("bar", formatXY(data), "linear", block)
    }

    // ------------------------------------------------------
    // CASE B: CATEGORY PLOTS (String Labels)
    // Usage: bar(mapOf("Mon" to 10, "Tue" to 20))
    // ------------------------------------------------------
    fun line(data: Map<String, Number>, block: LineDatasetScope.() -> Unit = {}) {
        addDataset("line", formatCategories(data), "category", block)
    }

    fun bar(data: Map<String, Number>, block: BarDatasetScope.() -> Unit = {}) {
        addDataset("bar", formatCategories(data), "category", block)
    }

    // ------------------------------------------------------
    // CASE C: SIMPLE LISTS (Index/Global Labels)
    // Usage: line(listOf(10, 20, 30)) -- relies on global labels() being set
    // ------------------------------------------------------
    fun line(data: List<Number>, block: LineDatasetScope.() -> Unit = {}) {
        addDataset("line", data.toTypedArray(), null, block)
    }

    fun bar(data: List<Number>, block: BarDatasetScope.() -> Unit = {}) {
        addDataset("bar", data.toTypedArray(), null, block)
    }


    // ------------------------------------------------------
    // PRIVATE HELPERS (The "Engine Room")
    // ------------------------------------------------------

    // Helper to genericize the add logic
    private fun <T : DatasetScope> addDataset(
        type: String,
        dataFormatted: Any, // Can be Array<{x,y}> or Array<Number>
        axisTypeHint: String?,
        block: T.() -> Unit
    ) {
        // 1. Update the global inference only if we haven't already
        if (inferredXAxisType == null && axisTypeHint != null) {
            inferredXAxisType = axisTypeHint
        }

        // 2. Create the correct scope based on string type
        val scope = when(type) {
            "line" -> LineDatasetScope()
            "bar" -> BarDatasetScope()
            "scatter" -> ScatterDatasetScope()
            else -> throw IllegalArgumentException("Unknown type")
        }

        // 3. Apply user config
        @Suppress("UNCHECKED_CAST")
        (scope as T).block()

        // 4. Attach data and save
        scope.config.data = dataFormatted
        datasets.add(scope.config)
    }

    // Helper: Convert Pairs to JS Objects {x: 1, y: 2}
    private fun formatXY(data: List<Pair<Number, Number>>): Array<dynamic> {
        return data.map { (xVal, yVal) ->
            jso { x = xVal; y = yVal }
        }.toTypedArray()
    }

    // Helper: Convert Map to JS Objects {x: "Label", y: 2}
    private fun formatCategories(data: Map<String, Number>): Array<dynamic> {
        return data.map { (key, value) ->
            jso { x = key; y = value }
        }.toTypedArray()
    }

    fun configScales(block: ScalesScope.() -> Unit) {
        val scalesObj = jso {}
        ScalesScope(scalesObj).block()
        options.scales = scalesObj
    }

    fun configTitle(text: String) {
        if (options.plugins == undefined) options.plugins = jso {}
        options.plugins.title = jso {
            display = true
            this.text = text
        }
    }

    // Inside ChartScope class...

    // --- 5. Legend Configuration (Built-in) ---
    fun configLegend(
        position: String = "top", // top, left, bottom, right
        align: String = "center", // start, center, end
        display: Boolean = true,
        padding: Int = 20,
    ) {
        // Ensure options.plugins exists
        if (options.plugins == undefined) options.plugins = jso {}

        options.plugins.legend = jso {
            this.display = display
            this.position = position
            this.align = align
            this.labels = jso {
                this.usePointStyle = true // Uses circles/squares instead of rectangles
                this.boxWidth = 10
                this.padding = padding
            }
        }
    }

    // --- 6. Generic Plugin Config (The "Escape Hatch") ---
    // Use this for Zoom, Annotations, or anything I didn't hardcode.
    fun configPlugin(pluginId: String, block: (dynamic) -> Unit) {
        if (options.plugins == undefined) options.plugins = jso {}

        if (options.plugins[pluginId] == undefined) {
            options.plugins[pluginId] = jso {}
        }

        // Pass the config object as 'it'
        block(options.plugins[pluginId])
    }

}

class ScalesScope(private val scalesObj: dynamic) {

    fun x(block: AxisScope.() -> Unit) {
        val axis = jso {}
        AxisScope(axis).block()
        scalesObj.x = axis
    }

    fun y(id: String = "y", block: AxisScope.() -> Unit) {
        val axis = jso {}
        AxisScope(axis).block()
        scalesObj[id] = axis
    }
}

class AxisScope(private val axisObj: dynamic) {
    var min: Number? = null
        set(value) { field = value; axisObj.min = value }

    var max: Number? = null
        set(value) { field = value; axisObj.max = value }

    var stacked: Boolean? = null
        set(value) { field = value; axisObj.stacked = value }

    fun title(text: String) {
        axisObj.title = jso {
            display = true
            this.text = text
        }
    }

    fun grid(display: Boolean) {
        axisObj.grid = jso { this.display = display }
    }

    fun ticks(block: TicksScope.() -> Unit) {
        val ticksObj = jso {}
        TicksScope(ticksObj).block()
        axisObj.ticks = ticksObj
    }
}

class TicksScope(private val ticksObj: dynamic) {
    var stepSize: Number? = null
        set(value) { field = value; ticksObj.stepSize = value }

    var precision: Int? = null
        set(value) { field = value; ticksObj.precision = value }
}
